import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, XCircle, ArrowRight, Home, RefreshCw } from 'lucide-react';
import axios from 'axios';

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const sessionId = searchParams.get('session_id');
  const refId = searchParams.get('ref');
  const API = process.env.REACT_APP_BACKEND_URL + '/api';

  useEffect(() => {
    if (sessionId) {
      axios.get(`${API}/stripe/session-status/${sessionId}`)
        .then(res => {
          setPaymentInfo(res.data);
        })
        .catch(err => {
          console.error('Failed to fetch session:', err);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: 'linear-gradient(135deg, #0B1F3A 0%, #1a3a5c 100%)' }}>
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 text-center">
        {loading ? (
          <div className="py-12">
            <RefreshCw size={48} className="animate-spin mx-auto text-[#94A3B8] mb-4" />
            <p className="text-[#475569]">Verifying payment...</p>
          </div>
        ) : (
          <>
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={40} className="text-green-600" />
            </div>
            
            <h1 className="font-heading text-2xl font-bold text-[#0B1F3A] mb-2">
              Payment Successful!
            </h1>
            
            <p className="text-[#475569] mb-6">
              Thank you for your payment. We've received your transaction and will begin processing your request.
            </p>

            {paymentInfo && (
              <div className="bg-gray-50 rounded-xl p-4 mb-6 text-left">
                <div className="flex justify-between py-2 border-b border-gray-200">
                  <span className="text-[#94A3B8] text-sm">Amount</span>
                  <span className="font-semibold text-[#0B1F3A]">
                    ${(paymentInfo.amount_total / 100).toFixed(2)} {paymentInfo.currency?.toUpperCase()}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-200">
                  <span className="text-[#94A3B8] text-sm">Status</span>
                  <span className="font-semibold text-green-600">
                    {paymentInfo.payment_status === 'paid' ? 'Paid' : paymentInfo.status}
                  </span>
                </div>
                {refId && (
                  <div className="flex justify-between py-2">
                    <span className="text-[#94A3B8] text-sm">Reference</span>
                    <span className="font-mono text-sm text-[#0B1F3A]">{refId}</span>
                  </div>
                )}
              </div>
            )}

            <div className="space-y-3">
              <Link to="/portal" className="flex items-center justify-center gap-2 w-full py-3 rounded-xl font-semibold text-white" style={{ background: '#0B1F3A' }}>
                Go to Client Portal <ArrowRight size={18} />
              </Link>
              <Link to="/" className="flex items-center justify-center gap-2 w-full py-3 rounded-xl font-semibold text-[#475569] border border-gray-200">
                <Home size={18} /> Return Home
              </Link>
            </div>

            <p className="text-xs text-[#94A3B8] mt-6">
              A confirmation email has been sent to your registered email address.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

const PaymentCancelPage = () => {
  const [searchParams] = useSearchParams();
  const refId = searchParams.get('ref');

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: 'linear-gradient(135deg, #0B1F3A 0%, #1a3a5c 100%)' }}>
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 text-center">
        <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-6">
          <XCircle size={40} className="text-amber-600" />
        </div>
        
        <h1 className="font-heading text-2xl font-bold text-[#0B1F3A] mb-2">
          Payment Cancelled
        </h1>
        
        <p className="text-[#475569] mb-6">
          Your payment was not completed. No charges have been made to your account.
        </p>

        {refId && (
          <div className="bg-gray-50 rounded-xl p-4 mb-6">
            <span className="text-[#94A3B8] text-sm">Reference: </span>
            <span className="font-mono text-sm text-[#0B1F3A]">{refId}</span>
          </div>
        )}

        <div className="space-y-3">
          <Link to="/contact" className="flex items-center justify-center gap-2 w-full py-3 rounded-xl font-semibold text-white" style={{ background: '#0B1F3A' }}>
            Contact Support <ArrowRight size={18} />
          </Link>
          <Link to="/" className="flex items-center justify-center gap-2 w-full py-3 rounded-xl font-semibold text-[#475569] border border-gray-200">
            <Home size={18} /> Return Home
          </Link>
        </div>

        <p className="text-xs text-[#94A3B8] mt-6">
          If you have questions about your payment, please contact our support team.
        </p>
      </div>
    </div>
  );
};

export { PaymentSuccessPage, PaymentCancelPage };
